import { Check, Loader2Icon, PlusIcon, Trash2, X } from "lucide-react";
import {
  type ComponentType,
  Fragment,
  type JSX,
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Control,
  ControllerRenderProps,
  FieldPath,
  FieldValues,
} from "react-hook-form";

import { MultiSelectProps, WpxMultiSelect } from "../forms/WpxMultiSelect";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";

import FormWrapper from "./FormWrapper";

import { ButtonVariant, LabelValue } from "@/app/app.interface";
import { DELETE_CONFIRMATION_CONSTANTS } from "@/app/constants/global-constants";
import Icons from "@/components/Icons";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandLoading,
} from "@/components/ui/command";
import CustomChevronIcon, { Side } from "@/components/ui/CustomChervonIcon";
import { FormControl, FormField } from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import * as Popover from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/ScrollArea";
import { Any } from "@/interfaces/globals";
import { showAlertDialog } from "@/redux/slices/alertSlice";
import { useAppDispatch } from "@/redux/store";
import { cn } from "@/utils/core/generalUtils";

export type Option = {
  label: string;
  url?: string;
  value: string | { label: string; id: string };
  customNode?: ReactNode;
};

type AdditionOption = {
  options?: Option[];
  showOptionInSelect?: boolean;
};

type SelectSingle<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = ClassName & {
  options?: Option[];
  groupedOptions?: {
    header: string;
    options: Option[];
  }[];
  placeholder: string;
  field?: ControllerRenderProps<TFieldValues, TName>;
  isLoading?: boolean;
  onFieldValueChange?: (value: string, label?: string) => void;
  defaultValue?: string;
  defaultLabel?: string;
  addButton?:
    | {
        label?: string;
        variant?: ButtonVariant;
        className?: string;
        handleClick?: () => void;
        icon?: string;
      }
    | false;
  deleteAction?: (id: string) => void;
  searchable?: boolean;
  disabled?: boolean;
  side?: Side;
  allowDuplicate?: boolean;
  isShowSearchInput?: boolean;
  isReadOnly?: boolean;
  isShowArrow?: boolean;
  isModal?: boolean;
  isPopoverContentWidthFull?: boolean;
  customIcon?: ReactNode;
  searchPlaceholder?: string;
  displayValue?: string;
  /*
   * Additional Options
   * additionOption?: {
   *   options?: Option[];
   *   showOptionInSelect?: boolean;
   * };
   */
  additionOption?: AdditionOption;
  renderOptionClassName?: string;
  /**
   * When `true`, allows options from both `options` and `groupedOptions` to be used together.
   * This is useful when you need to support a mix of grouped and ungrouped options in the same
   * select component, instead of enforcing a strictly grouped-only or ungrouped-only list.
   */
  allowMixedOptions?: boolean;
  /**
   * Controls the visual ordering of grouped vs ungrouped options when both are present.
   *
   * - `"ungrouped-first"`: Render ungrouped options before any grouped sections.
   * - `"grouped-first"`: Render grouped sections before any ungrouped options.
   */
  groupOrder?: "ungrouped-first" | "grouped-first";
  showCheckIcon?: boolean;
  clearableConfig?: {
    isClearable?: boolean;
    onClear?: () => void;
  };
};

type OmmitedMultiSelectProps = Omit<MultiSelectProps, "onValueChange">;
interface SelectMultiProps extends OmmitedMultiSelectProps {
  control?: Control<Any>;
  name?: string;
  label?: string;
  isRequired?: boolean;
  defaultValue?: string[];
  node?: {
    position: "right";
    node: ComponentType<{ id: string }>;
  };
  withoutform?: {
    values: string[];
    onValueChange: (value: LabelValue[]) => void;
  };
  maxCount?: number;
  displayAll?: boolean;
  showRemoveAllIcon?: boolean;
  portal?: boolean;
}

const SelectMulti = (props: SelectMultiProps): JSX.Element => {
  const {
    options,
    placeholder,
    isLoading,
    defaultValue,
    name,
    label,
    isRequired,
    maxCount = 2,
    ...rest
  } = props;

  if (props.withoutform) {
    return (
      <section className='space-y-2'>
        {label && (
          <Label
            required={isRequired}
            className='font-normal flex items-start gap-x-[0.5px] pl-1'
          >
            {label}
          </Label>
        )}
        <WpxMultiSelect
          placeholder={placeholder}
          defaultValue={props.withoutform.values ?? []}
          // Values={props.withoutform.values}
          options={options}
          isLoading={isLoading}
          onValueChange={(value) => props.withoutform?.onValueChange(value)}
          maxCount={maxCount}
          {...rest}
        />
      </section>
    );
  }

  return (
    <FormField
      name={name || ""}
      control={props.control}
      render={({ field }) => (
        <FormWrapper label={label} isRequired={isRequired}>
          <WpxMultiSelect
            placeholder={placeholder}
            defaultValue={defaultValue || []}
            Values={field.value}
            options={options}
            isLoading={isLoading}
            onValueChange={(value) => field.onChange(value)}
            maxCount={maxCount}
            {...rest}
          />
        </FormWrapper>
      )}
    />
  );
};

// New Component
const FormSelectSingle = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>(
  props: SelectSingle<TFieldValues, TName> & {
    withoutform?: boolean;
    popoverClassName?: string;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    portal?: boolean;
  },
): JSX.Element => {
  const {
    placeholder,
    options,
    field,
    className,
    popoverClassName,
    open: controlledOpen,
    onOpenChange,
    portal = false,
    isLoading,
    onFieldValueChange,
    defaultValue,
    defaultLabel,
    withoutform,
    addButton,
    deleteAction,
    disabled,
    groupedOptions,
    side,
    isShowSearchInput = true,
    isReadOnly,
    isShowArrow = true,
    isModal = false,
    isPopoverContentWidthFull = true,
    customIcon,
    additionOption,
    searchPlaceholder = "Search...",
    renderOptionClassName,
    allowMixedOptions,
    groupOrder = "grouped-first",
    showCheckIcon = true,
    clearableConfig = { isClearable: true },
  } = props;

  const dispatch = useAppDispatch();
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const [search, setSearch] = useState("");
  const commandListRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const constructedOptions = useMemo(() => {
    const safeOptions = options ?? [];
    if (additionOption?.showOptionInSelect && additionOption?.options?.length) {
      const map = new Map<string | number, Option>();
      [...safeOptions, ...additionOption.options].forEach((opt) => {
        const id =
          typeof opt.value === "object" && opt.value !== null
            ? opt.value.id
            : opt.value;
        if (!map.has(id)) {
          map.set(id, opt);
        }
      });

      return Array.from(map.values());
    }

    return safeOptions;
  }, [options, additionOption]);

  const selectedOption = useMemo(() => {
    let pool: Option[];
    if (allowMixedOptions) {
      pool = [
        ...constructedOptions,
        ...(groupedOptions?.flatMap((g) => g.options) ?? []),
      ];
    } else if (groupedOptions?.length) {
      pool = groupedOptions.flatMap((g) => g.options);
    } else {
      pool = constructedOptions;
    }

    return pool.find((opt) => {
      const id = typeof opt.value === "object" ? opt.value?.id : opt.value;

      return field?.value ? id === field.value : id === defaultValue;
    });
  }, [
    constructedOptions,
    groupedOptions,
    field?.value,
    defaultValue,
    allowMixedOptions,
  ]);

  const displayLabel =
    selectedOption?.label ||
    (field?.value && defaultLabel ? defaultLabel : undefined);

  const filteredOptions = useMemo(() => {
    if (!search.trim()) {
      return constructedOptions;
    }
    const q = search.toLowerCase().trim();

    return constructedOptions.filter((opt) => {
      const labelMatch = opt.label.toLowerCase().includes(q);
      const valueMatch =
        typeof opt.value === "string"
          ? opt.value.toLowerCase().includes(q)
          : opt.value && opt.value.label.toLowerCase().includes(q);

      return labelMatch || valueMatch;
    });
  }, [constructedOptions, search]);

  const filteredGroupedOptions = useMemo(() => {
    if (!groupedOptions || !search.trim()) {
      return groupedOptions;
    }

    const q = search.toLowerCase().trim();

    return groupedOptions
      .map((group) => {
        const filteredOptions = group.options.filter((opt) => {
          const labelMatch = opt.label.toLowerCase().includes(q);
          const valueMatch =
            typeof opt.value === "string"
              ? opt.value.toLowerCase().includes(q)
              : opt.value.label.toLowerCase().includes(q);

          return labelMatch || valueMatch;
        });

        return {
          ...group,
          options: filteredOptions,
        };
      })
      .filter(
        (g) => g.options.length > 0 || g.header.toLowerCase().includes(q),
      );
  }, [groupedOptions, search]);
  const handleSelect = useCallback(
    (currentLabel: string, optionValue: string) => {
      if (onFieldValueChange) {
        onFieldValueChange(optionValue, currentLabel);
      } else {
        field?.onChange(optionValue);
      }
      setInternalOpen(false);
      onOpenChange?.(false);
      setSearch("");
    },
    [onFieldValueChange, field, onOpenChange],
  );

  const handleTriggerKeyDown = useCallback(
    (e: KeyboardEvent<HTMLElement>) => {
      if (disabled || isReadOnly) {
        return;
      }

      if (e.key === "Tab" && !open) {
        e.preventDefault();
        setInternalOpen(true);
        onOpenChange?.(true);
      } else if (e.key === "Escape" && open) {
        e.preventDefault();
        e.stopPropagation();
        setInternalOpen(false);
        onOpenChange?.(false);
        setSearch("");
        triggerRef.current?.focus();
      }
    },
    [disabled, isReadOnly, onOpenChange, open],
  );

  const handleContentKeyDown = useCallback(
    (e: KeyboardEvent<HTMLElement>) => {
      if (e.key === "Tab") {
        setInternalOpen(false);
        onOpenChange?.(false);
        setSearch("");
      } else if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        setInternalOpen(false);
        onOpenChange?.(false);
        setSearch("");
        triggerRef.current?.focus();
      }
    },
    [onOpenChange],
  );

  const handleDeleteOption = useCallback(
    (id: string): void => {
      dispatch(
        showAlertDialog({
          title: DELETE_CONFIRMATION_CONSTANTS.title,
          desc: DELETE_CONFIRMATION_CONSTANTS.optionTitle,
          btnName: DELETE_CONFIRMATION_CONSTANTS.btnName,
          cancelName: DELETE_CONFIRMATION_CONSTANTS.cancelName,
          btnAction: () => {
            if (deleteAction) {
              deleteAction(id);
            }
          },
        }),
      );
    },
    [dispatch, deleteAction],
  );

  const renderOptions = (
    options: Option[],
    className?: string,
  ): JSX.Element => {
    return (
      <>
        {options?.map((option, index) => {
          const optionValue =
            typeof option?.value === "object"
              ? option?.value?.id
              : option?.value;
          const isSelected = selectedOption?.value === option?.value;

          return (
            <CommandItem
              key={`${optionValue}-${index}`}
              value={CSS.escape(option?.label) + index}
              onSelect={(currentLabel) =>
                handleSelect(currentLabel, optionValue)
              }
              className={cn(
                "p-2 content-stretch group cursor-pointer text-sm justify-between data-[selected=true]:bg-transparent hover:bg-accent",
                isSelected && "!bg-[#2844bd19]",
                className,
              )}
            >
              {option?.url && (
                <img
                  src={option?.url}
                  alt='user'
                  className='object-cover w-8 h-8 mr-2 rounded-md'
                />
              )}
              <div className='flex flex-col items-start gap-0'>
                <span className='line-clamp-4' title={option?.label}>
                  {option?.label}
                </span>
                {option?.customNode}
              </div>
              {isSelected && showCheckIcon && (
                <Check className='ml-2 w-4 h-4 self-center' />
              )}
              {deleteAction && (
                <div
                  className='invisible p-1 cursor-pointer group-hover:visible'
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDeleteOption(optionValue);
                  }}
                >
                  <Trash2 color='red' size={18} />
                </div>
              )}
            </CommandItem>
          );
        })}
      </>
    );
  };

  const renderContent = (): JSX.Element | JSX.Element[] | undefined => {
    if (allowMixedOptions && search) {
      return (
        <>
          {/* Ungrouped section */}
          {groupOrder === "ungrouped-first" && filteredOptions.length > 0 && (
            <CommandGroup className='px-0 !mx-0'>
              {renderOptions(filteredOptions, renderOptionClassName)}
            </CommandGroup>
          )}

          {/* Grouped sections */}
          {filteredGroupedOptions?.map((group) => (
            <Accordion
              key={group.header}
              type='multiple'
              className='w-full mt-2'
              defaultValue={[group.header]}
            >
              <AccordionItem value={group.header} className='border-none'>
                <AccordionTrigger
                  className='text-sm py-2 px-2 rounded-sm font-medium hover:bg-accent hover:no-underline'
                  iconClassName='w-3.5 h-3.5 ml-2'
                >
                  {group.header}
                </AccordionTrigger>
                <AccordionContent className='pb-0 border-l ml-3'>
                  {renderOptions(group.options, "pl-6")}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          ))}

          {/* Ungrouped last */}
          {groupOrder === "grouped-first" && filteredOptions.length > 0 && (
            <CommandGroup className='px-0 !mx-0'>
              {renderOptions(filteredOptions, renderOptionClassName)}
            </CommandGroup>
          )}
        </>
      );
    }
    if (allowMixedOptions) {
      return (
        <>
          {/* Ungrouped section */}
          {groupOrder === "ungrouped-first" && filteredOptions.length > 0 && (
            <CommandGroup className='px-0 !mx-0'>
              {renderOptions(filteredOptions, renderOptionClassName)}
            </CommandGroup>
          )}

          {/* Grouped sections */}
          {filteredGroupedOptions?.map((group) => (
            <Accordion
              key={group.header}
              type='single'
              collapsible
              className='w-full mt-2'
              defaultValue={
                group.options.some((o) => o.label === selectedOption?.label)
                  ? group.header
                  : undefined
              }
            >
              <AccordionItem value={group.header} className='border-none'>
                <AccordionTrigger
                  className='text-sm py-2 px-2 rounded-sm font-medium hover:bg-accent hover:no-underline'
                  iconClassName='w-3.5 h-3.5 ml-2'
                >
                  {group.header}
                </AccordionTrigger>
                <AccordionContent className='pb-0 border-l ml-3'>
                  {renderOptions(group.options, "pl-6")}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          ))}

          {/* Ungrouped last */}
          {groupOrder === "grouped-first" && filteredOptions.length > 0 && (
            <CommandGroup className='px-0 !mx-0'>
              {renderOptions(filteredOptions, renderOptionClassName)}
            </CommandGroup>
          )}
        </>
      );
    }

    if (groupedOptions) {
      return filteredGroupedOptions?.map((group) => (
        <Accordion
          key={group.header}
          type='single'
          collapsible
          className='w-full mt-2'
          defaultValue={
            group.options.some((o) => o.label === selectedOption?.label)
              ? group.header
              : undefined
          }
        >
          <AccordionItem value={group.header} className='border-none'>
            <AccordionTrigger
              className='text-sm py-2 px-2 rounded-sm font-medium hover:bg-accent'
              iconClassName='w-3.5 h-3.5 ml-2'
            >
              {group.header}
            </AccordionTrigger>
            <AccordionContent>
              {renderOptions(group.options, "pl-6")}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      ));
    }

    return (
      <CommandGroup className='px-0 !mx-0'>
        {renderOptions(filteredOptions, renderOptionClassName)}
      </CommandGroup>
    );
  };

  const Comp = withoutform ? Fragment : FormControl;
  const ExtendedComp = isReadOnly ? "div" : Popover.PopoverTriggerButton;

  return (
    <Popover.Popover
      open={open}
      onOpenChange={(o) => {
        setInternalOpen(o);
        onOpenChange?.(o);
        setSearch("");
      }}
      modal={isModal}
    >
      <Comp>
        <ExtendedComp
          ref={isReadOnly ? undefined : (triggerRef as Any)}
          className={cn(className)}
          disabled={disabled}
          side={side}
          isArrowVisible={isShowArrow && !displayLabel}
          title={displayLabel}
          onKeyDown={isReadOnly ? undefined : handleTriggerKeyDown}
          customIcon={
            clearableConfig.isClearable && displayLabel ? (
              <X
                className='ml-2 h-4 w-4 opacity-50 hover:text-red-500'
                onClick={(e) => {
                  e.stopPropagation();
                  if (clearableConfig.onClear) {
                    clearableConfig.onClear();
                  } else {
                    if (onFieldValueChange) {
                      onFieldValueChange("", "");
                    } else {
                      field?.onChange("");
                    }
                  }
                }}
              />
            ) : (
              customIcon
            )
          }
        >
          <span
            className={cn(
              "break-all",
              !displayLabel && "opacity-50 text-muted-foreground",
            )}
          >
            {displayLabel || placeholder}
          </span>
          {isReadOnly && isShowArrow && (
            <CustomChevronIcon
              className='ml-2 h-4 w-4 opacity-50'
              side={side}
            />
          )}
        </ExtendedComp>
      </Comp>

      <Popover.PopoverContent
        className={cn(
          "p-2 border border-input rounded-[0.5rem] shadow-select z-[60] min-w-fit",
          isPopoverContentWidthFull && "popover-content-width-full",
        )}
        portal={portal}
        align='start'
        side={side}
        onKeyDown={handleContentKeyDown}
      >
        <Command
          shouldFilter={false}
          className={popoverClassName}
          defaultValue='-'
        >
          <CommandList ref={commandListRef}>
            {isShowSearchInput && !isLoading && (
              <CommandInput
                placeholder={searchPlaceholder}
                autoFocus
                value={search}
                onValueChange={(value) => {
                  setSearch(value);
                  setTimeout(() => {
                    commandListRef.current?.scrollTo({
                      top: 0,
                      behavior: "smooth",
                    });
                  });
                }}
              />
            )}
            {isLoading ? (
              <CommandLoading>
                <Loader2Icon className='w-6 h-6 animate-spin' />
              </CommandLoading>
            ) : (
              <ScrollArea className='overflow-y-scroll max-h-44 select-scrollbar-hide'>
                {renderContent()}
              </ScrollArea>
            )}
            {!isLoading &&
              !filteredOptions?.length &&
              !filteredGroupedOptions?.length && (
                <div className='py-6 text-center text-sm text-muted-foreground break-all'>
                  No results found {search && `for "${search}"`}
                </div>
              )}
          </CommandList>
          {addButton && (
            <Button
              type='button'
              className={cn(
                "flex justify-center items-center w-full bg-[#F2F4F7] text-sm tracking-wide space-x-2 font-medium",
                addButton.className,
              )}
              variant={addButton?.variant || "secondary"}
              onClick={() => {
                addButton.handleClick?.();
                setInternalOpen(false);
                onOpenChange?.(false);
              }}
            >
              {addButton.icon ? (
                <Icons name={addButton.icon} size={18} color='#fff' />
              ) : (
                <PlusIcon size={18} strokeWidth={1.8} className='mx-1' />
              )}
              <p className={addButton.icon && "px-3"}>{addButton?.label}</p>
            </Button>
          )}
        </Command>
      </Popover.PopoverContent>
    </Popover.Popover>
  );
};

export { FormSelectSingle, SelectMulti };
